<template>
    <div>
        <h1 class="font-medium text-left text-gray-900">
            {{ $t("device.header.co2Toggle") }}
        </h1>

        <dd class="mt-1 text-sm text-gray-900">
            <ul class="divide-gray-200 rounded-md">
                <li class="flex items-center justify-between py-3 pr-4 text-sm">
                    <Toggle v-model="co2Active" :disabled="!isEditAllowed" :caption="co2Active ? $t('dailyCheck.form.active') : $t('dailyCheck.form.inactive')" />
                </li>

                <li v-if="co2Active" class="flex items-center justify-between py-3 pr-4 text-sm">
                    <Toggle v-model="dayNightModeActive" :disabled="!isEditAllowed" :caption="dayNightModeActive ? $t('device.co2Settings.nightModeActive') : $t('device.co2Settings.nightModeInactive')" />
                </li>

                <li v-if="co2Active && dayNightModeActive" class="flex items-center justify-between py-3 pr-4 text-sm">
                    <div class="flex items-center flex-1 w-0">
                        <CogIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                        <div class="flex flex-col flex-1 w-0 ml-4">
                            <span>{{ $t("device.co2Settings.nightMode") }}</span>
                        </div>
                    </div>
                    <div class="relative flex-1 ml-4">
                        <RadioGroup v-model="dayNightMode" class="mt-2">
                            <div class="grid grid-cols-2 gap-3">
                                <RadioGroupOption v-for="option in nightModeOptions" :key="option.value" :value="option" as="template" v-slot="{ active, checked }">
                                    <div :class="[
                                        'cursor-pointer focus:outline-none',
                                        active ? 'ring-2 ring-indigo-600 ring-offset-2' : '',
                                        checked
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                                            : 'ring-1 ring-inset ring-gray-300 bg-white text-gray-900 hover:bg-gray-50',
                                        'flex items-center justify-center rounded-md py-2 px-1 text-sm sm:flex-1',
                                    ]">
                                        <RadioGroupLabel as="span">{{ option.name }}</RadioGroupLabel>
                                    </div>
                                </RadioGroupOption>
                            </div>
                        </RadioGroup>
                    </div>
                </li>

                <li v-if="co2Active && dayNightModeActive" class="items-center justify-between py-3 pr-4 mb-8 text-sm">
                    <div class="flex items-center flex-1 mb-4">
                        <CogIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                        <div class="flex flex-col flex-1 w-0 ml-4">
                            <span class="flex-1">{{ $t("device.co2Settings.intervalSelection") }}</span>
                            <span class="text-xs text-gray-400">
                                {{ getIntervalDescription() }}
                            </span>
                        </div>
                    </div>
                    <DayNightModeSlider v-if="isTimeSliderVisible" :key="sliderKey" :selection="selectedTimeIntervals" @emitChange="selectionChangeHandler" />
                </li>
            </ul>
        </dd>
    </div>
</template>

<script>
import { computed, ref, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { hasDeviceTenantPermission, USER_PERMISSIONS } from "@/lib/userHasPermission";
import Toggle from "@/components/ToggleButton";
import { CogIcon } from "@heroicons/vue/outline/";
import DayNightModeSlider from "@/components/sliders/DayNightModeSlider";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";

export default {
    name: "DeviceCo2Setting",
    components: {
        Toggle,
        CogIcon,
        DayNightModeSlider,
        RadioGroup,
        RadioGroupLabel,
        RadioGroupOption
    },

    props: {
        refresh: {
            type: Boolean,
            default: false
        }
    },
    emits: ["updateConfig"],

    setup(props, { emit }) {
        const { t } = useI18n();
        const store = useStore();
        const device = computed(() => store.state.device.current);
        const sliderKey = ref(0);
        const confirmOpen = ref(false);
        const co2Active = ref(false);
        const dayNightModeActive = ref(false);
        const selectedTimeIntervals = ref(["08:00", "20:00"]);

        const nightModeOptions = computed(() => [
            { name: t("device.co2Settings.nightModes.dimmed"), value: "dim" },
            { name: t("device.co2Settings.nightModes.off"), value: "off" }
        ]);
        const dayNightMode = ref(nightModeOptions.value[0]);

        const isEditAllowed = computed(() =>
            hasDeviceTenantPermission(USER_PERMISSIONS.ADMIN)
        );

        const isTimeSliderVisible = computed(() =>
            selectedTimeIntervals.value != null && co2Active.value && dayNightModeActive.value
        );

        const selectionChangeHandler = (newSelection) => {
            selectedTimeIntervals.value = newSelection;
        };

        const getIntervalDescription = () => {
            if (dayNightMode.value?.value === "dim") {
                return t("device.co2Settings.intervalDescriptionDimmed", {
                    start: selectedTimeIntervals.value[1],
                    end: selectedTimeIntervals.value[0],
                });
            } else {
                return t("device.co2Settings.intervalDescriptionOff", {
                    start: selectedTimeIntervals.value[1],
                    end: selectedTimeIntervals.value[0]
                });
            }
        };

        const emitConfigUpdate = () => {
            emit("updateConfig", {
                enabled: co2Active.value,
                dayNightMode: dayNightModeActive.value ? dayNightMode.value.value : "bright",
                dayNightTimeIntervals: selectedTimeIntervals.value
            });
        };

        const reset = async () => {
            if (!device.value?.configV2?.co2_app) return;

            co2Active.value = device.value.configV2.co2_app.enabled || false;
            const sceneMode = device.value.configV2.co2_app.scene_mode || "bright";
            dayNightModeActive.value = sceneMode !== "bright";

            if (sceneMode === "dim") {
                dayNightMode.value = nightModeOptions.value[0];
            } else if (sceneMode === "off") {
                dayNightMode.value = nightModeOptions.value[1];
            }

            if (device.value.configV2.system?.ui_state) {
                let startDay = device.value.configV2.system.ui_state.start_day?.toString().substring(0, 5) || "08:00";
                let startNight = device.value.configV2.system.ui_state.start_night?.toString().substring(0, 5) || "20:00";

                if (startDay === "08:05") startDay = "08:00";
                if (startNight === "20:05") startNight = "20:00";

                selectedTimeIntervals.value = [startDay, startNight];
            } else {
                selectedTimeIntervals.value = ["08:00", "20:00"];
            }
            sliderKey.value++;
            await nextTick();
        };

        watch(device, (newDevice) => {
            if (newDevice) reset();
        }, { immediate: true });

        watch(() => props.refresh, (shouldRefresh) => {
            if (shouldRefresh) reset();
        });

        watch([co2Active, dayNightModeActive, dayNightMode, selectedTimeIntervals], emitConfigUpdate, { deep: true });

        return {
            device,
            confirmOpen,
            sliderKey,
            co2Active,
            dayNightModeActive,
            dayNightMode,
            selectedTimeIntervals,
            isEditAllowed,
            isTimeSliderVisible,
            nightModeOptions,
            selectionChangeHandler,
            getIntervalDescription
        };
    },
};
</script>
